import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { ButtonIconDotMenu } from '../button/ButtonIconDotMenu.js';
import { Content } from './Content.js';
import { ContentMobile } from './ContentMobile.js';
import { DropdownItemIconContainer, DropdownItemBase, Item } from './Item.js';
import { cssVar } from '../../theme/variables.js';
import { Separator } from '@radix-ui/react-context-menu';
import { Trigger, Group, Label, Root, Portal } from '@radix-ui/react-dropdown-menu';
import React__default from 'react';
import { styled } from 'styled-components';
import { mediaMobile, mediaNotMobile } from '../../constants/styled-utils.js';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { useResponsive } from '../../hooks/useResponsive.js';

const DropdownSeparator = styled(Separator).withConfig({
  displayName: "DropdownMenu__DropdownSeparator",
  componentId: "sc-5b080a5d-0"
})([
  `height:1px;background:`,
  `;margin:0.25rem 0.75rem;`
], cssVar["color-border-tertiary"]);
const StyledTrigger = styled(Trigger).withConfig({
  displayName: "DropdownMenu__StyledTrigger",
  componentId: "sc-5b080a5d-1"
})([
  `outline:none !important;`
]);
const OverrideTrigger = /* @__PURE__ */ React__default.forwardRef(function DropdownTriggerOverride({ onPointerDown, onPointerUp, onOpenChange, ...props }, ref) {
  return /* @__PURE__ */ jsx(StyledTrigger, {
    onPointerDown: (e) => {
      if (e.pointerType === "touch") {
        e.preventDefault();
      }
      onPointerDown?.(e);
    },
    onPointerUp: (e) => {
      if (e.pointerType === "touch" && onOpenChange && !e.defaultPrevented) {
        onOpenChange((state) => !state);
      }
      onPointerUp?.(e);
    },
    ...props,
    ref
  });
});
const DropdownMenuBase = ({ children, renderTriggerElement, contentProps, openProps, onEscapeKeyDown, forceDrawer = false, modal = true }) => {
  const { isMobileView } = useResponsive();
  const [open, onOpenChange] = React__default.useState(false);
  const openControls = {
    open: openProps?.open ?? open,
    onOpenChange: openProps?.onOpenChange ?? onOpenChange
  };
  return /* @__PURE__ */ jsxs(Root, {
    ...openControls,
    modal,
    children: [
      renderTriggerElement(openControls),
      /* @__PURE__ */ jsx(Portal, {
        container: getDefaultRadixPortalContainer(),
        children: isMobileView || forceDrawer ? /* @__PURE__ */ jsx(ContentMobile, {
          onEscapeKeyDown,
          onOpenChange: openControls.onOpenChange,
          children
        }) : /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx("div", {
              style: {
                position: "fixed",
                zIndex: 100,
                inset: 0
              }
            }),
            /* @__PURE__ */ jsx(Content, {
              onEscapeKeyDown,
              align: "end",
              sideOffset: 4,
              ...contentProps,
              children
            })
          ]
        })
      })
    ]
  });
};
const DropdownGroup = styled(Group).withConfig({
  displayName: "DropdownMenu__DropdownGroup",
  componentId: "sc-5b080a5d-2"
})([
  ``,
  `{padding:0 1rem;& + &{margin-top:1.25rem;}}`
], mediaMobile);
const SimplifiedIconDotMenu = ButtonIconDotMenu;
const DropdownGroupTitle = styled(DropdownGroup).withConfig({
  displayName: "DropdownMenu__DropdownGroupTitle",
  componentId: "sc-5b080a5d-3"
})([
  ``,
  `{display:none;}`
], mediaNotMobile);
const TriggerButtonThreeDots = styled(SimplifiedIconDotMenu).attrs((props) => {
  return {
    ...props,
    as: OverrideTrigger
  };
}).withConfig({
  displayName: "DropdownMenu__TriggerButtonThreeDots",
  componentId: "sc-5b080a5d-4"
})([
  ``
]);
const TriggerButtonThreeDotsSlim = styled(SimplifiedIconDotMenu).attrs((props) => {
  return {
    ...props,
    as: OverrideTrigger
  };
}).withConfig({
  displayName: "DropdownMenu__TriggerButtonThreeDotsSlim",
  componentId: "sc-5b080a5d-5"
})([
  `width:1.5rem;padding:0;`
]);
const DropdownLabel = styled(Label).withConfig({
  displayName: "DropdownMenu__DropdownLabel",
  componentId: "sc-5b080a5d-6"
})([
  `display:flex;justify-content:space-between;align-items:center;align-self:stretch;color:`,
  `;font-size:calc(var(--base-font-size) - 1px);font-weight:700;text-transform:uppercase;padding:0.25rem 0.75rem;`,
  `{font-weight:600;font-size:1.25rem;line-height:1.5rem;letter-spacing:-0.45px;color:`,
  `;padding:1rem;text-transform:none;}`
], cssVar["color-text-tertiary"], mediaMobile, cssVar["color-text-primary"]);
const DropdownMenu = {
  Root,
  Base: DropdownMenuBase,
  Content,
  TriggerButtonThreeDots,
  TriggerButtonThreeDotsSlim,
  Trigger: OverrideTrigger,
  Item,
  ItemBase: DropdownItemBase,
  ItemIconContainer: DropdownItemIconContainer,
  Separator: DropdownSeparator,
  Group: DropdownGroup,
  GroupTitle: DropdownGroupTitle,
  TitleGroup: DropdownGroup,
  Label: DropdownLabel,
  IconContainer: DropdownItemIconContainer
};

export { DropdownMenu, SimplifiedIconDotMenu };
//# sourceMappingURL=DropdownMenu.js.map
